'use client';
import React from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader } from '@/components/ui/shadcn-io/ai/loader';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
    ChevronLeft,
    Lock,
    Plus,
    MessageSquare,
    PanelLeftOpen,
    Pin,
    PinOff,
    Trash2,
    MoreHorizontal,
    Pencil,
    Loader2,
    Settings,
    Download,
    FileText,
    File,
    Copy,
    Search,
    Folder,
    SlidersHorizontal,
    Bot,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { isTeamsNavLockedClient, DEFAULT_PLAN_POST_LOGIN_PATH } from '@/services/auth-service';
import { usePartnerBranding } from '@/hooks/use-partner-branding';
import { PoweredByImplementAi } from '@/components/shared/team-switcher';
import {
    useSidebar as useAppSidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useSidebar, useCommandTeamDrawer } from './sidebar-context';
import { NavUser } from '@/components/shared/nav-user';
import { NavOrg } from '@/components/shared/nav-org';
import { PlatformData } from '@/components/shared/sidbar-items';
import { useDebouncedValue } from '@tanstack/react-pacer';
import { SEARCH_DEBOUNCE_WAIT } from '@/utils/global';
import { findCachedCommandSession, useCommandSessions } from '@/hooks/use-command-sessions';
import { useCommandAgents } from '@/hooks/use-command-agents';
import { CommandSession, CommandAgent } from '@/types/command-agent';
import { DeleteConfirmationDialog } from '@/components/shared/delete-dialog';
import { PermissionGate } from '@/components/shared/permission-gate';
import { formatSessionSidebarTime } from '@/utils/command-session';
import { AccessDeniedCard } from '@/components/shared/access-denied-card';
import {
    CommandOtherChats,
    CommandOtherChatsHeader,
    CommandProjectsFolders,
    CommandProjectsHeader,
    notifySessionMoved,
    useAllCommandProjectsForMove,
} from '@/components/aios-command/chat/command-projects-section';
import { useIsMobile } from '@/hooks/use-mobile';
import { useProtectedMutation } from '@/hooks/use-protected-mutation';
import { del, post } from '@/utils/api-handler';
import apiClient from '@/utils/api-client';
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Image from 'next/image';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { CreateCommandAgent, EditCommandAgent } from '@/components/command-team';

type ExportSessionFormat = 'MARKDOWN' | 'PDF' | 'CLIPBOARD';

type ExportSessionVariables = {
    agentId: string;
    sessionId: string;
    format: ExportSessionFormat;
};

interface CommandAgentsSidebarPage {
    items: CommandAgent[];
}

type CommandAgentsSidebarCache = InfiniteData<CommandAgentsSidebarPage> | undefined;

interface ActivateAgentVariables {
    agentId: string;
    name: string;
    previousAgentId: string | null;
}

interface ActivateAgentMutationContext {
    previousSidebarData: Array<[QueryKey, CommandAgentsSidebarCache]>;
}


interface ChatSidebarProps {
    agentId: string | null;
    selectedAgentId?: string | null;
    activeSessionId: string | null;
    onSessionSelect: (sessionId: string | null, agentId?: string | null) => void;
    onNewSessionCreated?: (sessionId: string) => void;
    onAgentSwitch?: (agentId: string) => void;
    typingTitle?: { sessionId: string; title: string } | null;
    /** Renders inside the platform mobile sheet beside the icon rail. */
    embeddedMobile?: boolean;
}

export function ChatSidebar({
    agentId,
    selectedAgentId,
    activeSessionId,
    onSessionSelect,
    onNewSessionCreated,
    onAgentSwitch,
    typingTitle,
    embeddedMobile = false,
}: ChatSidebarProps) {
    const { sidebarOpen, toggleSidebar } = useSidebar();
    const { setOpenMobile } = useAppSidebar();
    const { resolvedTheme } = useTheme();
    const isMobile = useIsMobile();

    const closeSidebarIfMobile = () => {
        if (!isMobile) return;
        if (embeddedMobile) {
            setOpenMobile(false);
            return;
        }
        if (sidebarOpen) {
            toggleSidebar();
        }
    };

    const panelExpanded = embeddedMobile || sidebarOpen;
    const user = useSelector((state: RootState) => state.user.user);
    const teamsLocked = isTeamsNavLockedClient(user);

    const commandLogoSrc =
        resolvedTheme === 'dark' ? '/aios_command_white.svg' : '/aiosc.svg';
    const queryClient = useQueryClient();
    const [screenSize, setScreenSize] = React.useState<'small' | 'medium' | 'large'>('large');
    const [displayedTitle, setDisplayedTitle] = React.useState<string>('');
    const [typingSessionId, setTypingSessionId] = React.useState<string | null>(null);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: SEARCH_DEBOUNCE_WAIT });

    const {
        allAgents: allQuickLaunchAgents,
        isLoadingAgents: isLoadingQuickLaunch,
        isFetchingNextPage: isFetchingNextQuickLaunch,
        hasNextPage: hasNextQuickLaunch,
        loadMoreRef: loadMoreQuickLaunchRef,
    } = useCommandAgents({ search: debouncedSearch || undefined });
    const requestedCurrentAgentId = selectedAgentId ?? agentId ?? null;
    const resolvedCurrentAgentId = React.useMemo(() => {
        const list = allQuickLaunchAgents ?? [];
        if (requestedCurrentAgentId) {
            const requested = list.find((a) => a.id === requestedCurrentAgentId);
            if (requested) return requested.id;
            return requestedCurrentAgentId;
        }
        if (list.length === 0) return null;
        return list.find((a) => a.is_active)?.id ?? null;
    }, [allQuickLaunchAgents, requestedCurrentAgentId]);
    const [pinnedActiveAgent, setPinnedActiveAgent] = React.useState<CommandAgent | null>(null);

    const displayedAgents = React.useMemo(() => {
        const list = allQuickLaunchAgents ?? [];
        if (!pinnedActiveAgent || list.some(a => a.id === pinnedActiveAgent.id)) return list;
        return [pinnedActiveAgent, ...list];
    }, [allQuickLaunchAgents, pinnedActiveAgent]);
    const showAgentsSkeleton = isLoadingQuickLaunch && displayedAgents.length === 0;
    const showAgentsEmpty = !showAgentsSkeleton && displayedAgents.length === 0;
    const agentsListContentRef = React.useRef<HTMLDivElement>(null);
    const [agentsScrollHeight, setAgentsScrollHeight] = React.useState(0);

    React.useLayoutEffect(() => {
        if (showAgentsEmpty || !panelExpanded) {
            setAgentsScrollHeight(0);
            return;
        }

        const node = agentsListContentRef.current;
        if (!node) return;

        const syncHeight = () => {
            setAgentsScrollHeight(Math.min(node.scrollHeight, 220));
        };

        syncHeight();
        const observer = new ResizeObserver(syncHeight);
        observer.observe(node);
        return () => observer.disconnect();
    }, [
        showAgentsEmpty,
        panelExpanded,
        showAgentsSkeleton,
        displayedAgents.length,
        isFetchingNextQuickLaunch,
    ]);

    const [showCreateDialog, setShowCreateDialog] = React.useState(false);
    const [showEditDialog, setShowEditDialog] = React.useState(false);
    const [editingAgent, setEditingAgent] = React.useState<CommandAgent | null>(null);
    const [activatingAgentId, setActivatingAgentId] = React.useState<string | null>(null);
    const effectiveAgentId = activatingAgentId ?? resolvedCurrentAgentId;

    React.useEffect(() => {
        if (!effectiveAgentId) return;
        const found = allQuickLaunchAgents?.find((a) => a.id === effectiveAgentId);
        if (found) setPinnedActiveAgent(found);
    }, [allQuickLaunchAgents, effectiveAgentId]);

    const [agentDeleteDialogOpen, setAgentDeleteDialogOpen] = React.useState(false);
    const [agentToDelete, setAgentToDelete] = React.useState<CommandAgent | null>(null);

    const deleteAgentMutation = useProtectedMutation({
        mutationFn: (agentId: string) => del(`/api/command-team/agents/${agentId}`),
        requiredPermissions: ['DELETE_COMMAND_AGENT'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['command-agents'] });
            queryClient.invalidateQueries({ queryKey: ['command-agents-sidebar'] });
            toast.success('Command Agent deleted', {
                description: `${agentToDelete?.name} has been deleted successfully`,
            });
            setAgentDeleteDialogOpen(false);
            setAgentToDelete(null);
        },
        onError: (error: any) => {
            toast.error('Failed to delete command agent', {
                description: error.response?.data?.message || 'An error occurred while deleting the command agent.',
            });
            setAgentDeleteDialogOpen(false);
            setAgentToDelete(null);
        },
    });

    const handleAgentDelete = (agent: CommandAgent) => {
        setAgentToDelete(agent);
        setAgentDeleteDialogOpen(true);
    };

    const handleAgentDeleteConfirm = () => {
        if (!agentToDelete) return;
        deleteAgentMutation.mutate(agentToDelete.id);
    };

    const handleAgentEdit = (agent: CommandAgent) => {
        setEditingAgent(agent);
        setShowEditDialog(true);
    };

    const activateAgentMutation = useProtectedMutation<
        unknown,
        unknown,
        ActivateAgentVariables,
        ActivateAgentMutationContext
    >({
        mutationFn: (data) => post(`/api/command-team/agents/${data.agentId}/activate`),
        requiredPermissions: ['UPDATE_COMMAND_AGENT'],
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: ['command-agents-sidebar'] });
            const previousSidebarData = queryClient.getQueriesData<CommandAgentsSidebarCache>({
                queryKey: ['command-agents-sidebar'],
            });

            queryClient.setQueriesData<CommandAgentsSidebarCache>(
                { queryKey: ['command-agents-sidebar'] },
                (old) => {
                    if (!old?.pages) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            items: page.items.map((item) => ({
                                ...item,
                                is_active: item.id === variables.agentId,
                            })),
                        })),
                    };
                },
            );

            return { previousSidebarData };
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['command-agents-sidebar'] });
            queryClient.invalidateQueries({ queryKey: ['command-agents'] });
            queryClient.invalidateQueries({ queryKey: ['command-sessions', variables.agentId] });
            setActivatingAgentId(null);
            toast.success(`${variables.name} activated`, {
                description: 'Agent is now active and ready to chat.',
            });
        },
        onError: (_error, variables, context) => {
            context?.previousSidebarData?.forEach(([queryKey, data]) => {
                queryClient.setQueryData(queryKey, data);
            });
            setActivatingAgentId(null);
            if (variables.previousAgentId) {
                onAgentSwitch?.(variables.previousAgentId);
            }
            toast.error('Failed to activate agent', {
                description: `Could not activate ${variables.name}. Please try again.`,
            });
        },
    });

    const handleActivateAgent = (agent: CommandAgent) => {
        const previousAgentId = resolvedCurrentAgentId;
        setActivatingAgentId(agent.id);
        onAgentSwitch?.(agent.id);
        closeSidebarIfMobile();
        activateAgentMutation.mutate({
            agentId: agent.id,
            name: agent.name,
            previousAgentId,
        });
    };

    const {
        allSessions,
        isLoadingSessions,
        isFetchingNextPage,
        loadMoreRef,
        canListSessions,
        createSession,
        isCreatingSession,
        creatingSessionProjectId,
        updateSession,
        isUpdatingSession,
        deleteSession,
        isDeletingSession,
    } = useCommandSessions(effectiveAgentId);

    const { projects: moveProjects, canListProjects } = useAllCommandProjectsForMove();

    const [editingSessionId, setEditingSessionId] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!typingTitle) {
            setTypingSessionId(null);
            setDisplayedTitle('');
            return;
        }

        const { sessionId, title } = typingTitle;
        setTypingSessionId(sessionId);
        setDisplayedTitle('');

        let currentIndex = 0;
        const typingInterval = setInterval(() => {
            if (currentIndex < title.length) {
                setDisplayedTitle(title.slice(0, currentIndex + 1));
                currentIndex++;
            } else {
                clearInterval(typingInterval);
                setTimeout(() => {
                    setTypingSessionId(null);
                    setDisplayedTitle('');
                }, 500);
            }
        }, 30);

        return () => clearInterval(typingInterval);
    }, [typingTitle, allSessions]);
    const [editTitle, setEditTitle] = React.useState<string>('');
    const [originalTitle, setOriginalTitle] = React.useState<string>('');
    const inputRef = React.useRef<HTMLInputElement>(null);
    const isStartingEditRef = React.useRef<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [sessionToDelete, setSessionToDelete] = React.useState<{
        id: string;
        title: string;
    } | null>(null);
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);

    const exportSessionMutation = useProtectedMutation<void, Error, ExportSessionVariables>({
        requiredPermissions: ['LIST_COMMAND_SESSIONS'],
        mutationFn: async ({ agentId: aid, sessionId, format }) => {
            const base = `/api/command-team/agents/${aid}/sessions/${sessionId}/export`;
            if (format === 'PDF') {
                const res = await apiClient.get(base, {
                    params: { format: 'PDF' },
                    responseType: 'blob',
                });
                const blob = res.data as Blob;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `command-session-${sessionId.slice(0, 8)}.pdf`;
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success('PDF downloaded');
                return;
            }
            if (format === 'MARKDOWN') {
                let text = '';
                try {
                    const jsonRes = await apiClient.get<unknown>(base, {
                        params: { format: 'MARKDOWN' },
                    });
                    const data = jsonRes.data;
                    if (typeof data === 'string') {
                        text = data;
                    } else if (data && typeof data === 'object') {
                        const o = data as Record<string, unknown>;
                        text = String(o.content ?? o.markdown ?? o.text ?? '');
                    }
                } catch {
                    text = '';
                }
                if (!text) {
                    const res = await apiClient.get(base, {
                        params: { format: 'MARKDOWN' },
                        responseType: 'blob',
                    });
                    const blob = res.data as Blob;
                    const ct = String(res.headers['content-type'] || '').toLowerCase();
                    if (ct.includes('application/json')) {
                        const json = JSON.parse(await blob.text());
                        text =
                            typeof json === 'string'
                                ? json
                                : String(json?.content ?? json?.markdown ?? json?.text ?? '');
                    } else {
                        text = await blob.text();
                    }
                }
                if (!text.trim()) {
                    throw new Error('EMPTY_EXPORT');
                }
                const outBlob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
                const url = URL.createObjectURL(outBlob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `command-session-${sessionId.slice(0, 8)}.md`;
                a.rel = 'noopener';
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
                toast.success('Markdown downloaded');
                return;
            }
            const res = await apiClient.get(base, { params: { format: 'CLIPBOARD' } });
            const data = res.data as Record<string, unknown> | string;
            const text =
                typeof data === 'string'
                    ? data
                    : String(
                          (data as Record<string, unknown>)?.content ??
                              (data as Record<string, unknown>)?.text ??
                              (data as Record<string, unknown>)?.markdown ??
                              ''
                      );
            if (!text.trim()) {
                throw new Error('EMPTY_EXPORT');
            }
            await navigator.clipboard.writeText(text);
            const narrow =
                typeof window !== 'undefined' &&
                window.matchMedia('(max-width: 1023px)').matches;
            if (narrow) {
                toast.success('Copied to clipboard', {
                    duration: 1500,
                });
            } else {
                toast.success('Copied to clipboard');
            }
        },
        onError: (error: any) => {
            if (error?.message === 'PERMISSION_DENIED' || error?.message === 'EMPTY_EXPORT') return;
            const msg = error.response?.data?.message;
            if (msg) toast.error(msg);
        },
    });

    const runExportSession = (sessionId: string, format: ExportSessionFormat) => {
        if (!effectiveAgentId) return;
        exportSessionMutation.mutate({ agentId: effectiveAgentId, sessionId, format });
    };

    const exportingSessionId =
        exportSessionMutation.isPending && exportSessionMutation.variables
            ? exportSessionMutation.variables.sessionId
            : null;

    React.useEffect(() => {
        if (editingSessionId && inputRef.current) {
            const focusInput = () => {
                if (inputRef.current && editingSessionId && document.activeElement !== inputRef.current) {
                    inputRef.current.focus();
                }
            };
            focusInput();
            const timeout1 = setTimeout(focusInput, 100);
            const timeout2 = setTimeout(focusInput, 300);

            return () => {
                clearTimeout(timeout1);
                clearTimeout(timeout2);
            };
        }
    }, [editingSessionId]);

    const justCreatedSessionRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (activeSessionId && justCreatedSessionRef.current === activeSessionId) {
            if (scrollAreaRef.current) {
                const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
                if (scrollContainer) {
                    requestAnimationFrame(() => {
                        scrollContainer.scrollTop = 0;
                    });
                }
            }
            justCreatedSessionRef.current = null;
        }
    }, [activeSessionId]);

    const handleSessionCreated = (data: CommandSession) => {
        if (data?.id) {
            justCreatedSessionRef.current = data.id;
            onSessionSelect(data.id, data.agent_id ?? effectiveAgentId);
            if (onNewSessionCreated) {
                onNewSessionCreated(data.id);
            }
            closeSidebarIfMobile();
        }
    };

    const isCreatingUnfiledSession =
        isCreatingSession && !creatingSessionProjectId;

    const handleCreateNewChat = () => {
        if (!effectiveAgentId) return;
        createSession(undefined, {
            onSuccess: handleSessionCreated,
        });
    };

    const handleCreateNewChatInProject = (projectId: string) => {
        if (!effectiveAgentId) return;
        createSession(
            { project_id: projectId },
            { onSuccess: handleSessionCreated }
        );
    };

    const handleMoveSessionToProject = (
        sessionId: string,
        projectId: string | null,
        sessionAgentId?: string | null
    ) => {
        const ownerAgentId =
            sessionAgentId ??
            findCachedCommandSession(queryClient, sessionId)?.agent_id ??
            effectiveAgentId;
        if (!ownerAgentId) return;
        updateSession(
            { sessionId, project_id: projectId, ownerAgentId },
            {
                onSuccess: () => {
                    notifySessionMoved(projectId, moveProjects);
                    if (
                        activeSessionId === sessionId &&
                        ownerAgentId !== resolvedCurrentAgentId
                    ) {
                        onSessionSelect(sessionId, ownerAgentId);
                    }
                },
            }
        );
    };

    const handlePinToggle = (session: CommandSession) => {
        const ownerAgentId =
            session.agent_id ??
            findCachedCommandSession(queryClient, session.id)?.agent_id ??
            effectiveAgentId;
        if (!ownerAgentId) return;
        updateSession({
            sessionId: session.id,
            pinned: !session.pinned,
            ownerAgentId,
        });
    };

    const handleDeleteClick = (sessionId: string, title?: string) => {
        const fromUnfiled = allSessions.find((session) => session.id === sessionId);
        setSessionToDelete({
            id: sessionId,
            title: title || fromUnfiled?.title || "session",
        });
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (!sessionToDelete) return;
        const ownerAgentId =
            findCachedCommandSession(queryClient, sessionToDelete.id)?.agent_id ??
            effectiveAgentId;
        if (!ownerAgentId) return;
        deleteSession(
            { sessionId: sessionToDelete.id, ownerAgentId },
            {
            onSuccess: () => {
                if (activeSessionId === sessionToDelete.id) {
                    onSessionSelect(null);
                }
                setDeleteDialogOpen(false);
                setSessionToDelete(null);
            },
        });
    };

    const handleStartEdit = (session: CommandSession) => {
        isStartingEditRef.current = true;
        const originalValue = session.title || 'Untitled';
        setEditingSessionId(session.id);
        setEditTitle('');
        setOriginalTitle(originalValue);
        setTimeout(() => {
            isStartingEditRef.current = false;
        }, 1000);
    };

    const handleSaveEdit = (sessionId: string) => {
        if (isStartingEditRef.current) {
            return;
        }
        if (editingSessionId !== sessionId) return;

        const ownerAgentId =
            findCachedCommandSession(queryClient, sessionId)?.agent_id ??
            effectiveAgentId;
        if (!ownerAgentId) return;

        const trimmedTitle = editTitle.trim();

        if (!trimmedTitle) {
            setEditingSessionId(null);
            setEditTitle('');
            setOriginalTitle('');
            return;
        }

        if (trimmedTitle === originalTitle) {
            setEditingSessionId(null);
            setEditTitle('');
            setOriginalTitle('');
            return;
        }

        updateSession(
            { sessionId, title: trimmedTitle, ownerAgentId },
            {
                onSuccess: () => {
                    setEditingSessionId(null);
                    setEditTitle('');
                    setOriginalTitle('');
                },
            }
        );
    };

    const handleCancelEdit = () => {
        setEditingSessionId(null);
        setEditTitle('');
        setOriginalTitle('');
    };

    const isTitleTruncated = (title: string | null | undefined) => {
        if (!title) return false;
        return title.length > 20;
    };

    const handleKeyDown = (e: React.KeyboardEvent, sessionId: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveEdit(sessionId);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelEdit();
        }
    };

    React.useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth;
            if (width < 768) {
                setScreenSize('small');
            } else if (width >= 768 && width < 1024) {
                setScreenSize('medium');
            } else {
                setScreenSize('large');
            }
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const getWidth = () => {
        if (sidebarOpen) {
            return 'flex-1 min-w-0';
        } else {
            if (screenSize === 'small') {
                return 'w-0 hidden';
            } else {
                return 'w-16';
            }
        }
    };

    const sidebarContent = (
        <>
                {panelExpanded ? (
                    <div className="p-4 flex items-center justify-between">
                        <div className="flex flex-col gap-0">
                                <Image
                                    key={commandLogoSrc}
                                    src={commandLogoSrc}
                                    alt="AiOS Command"
                                    width={170}
                                    height={170}
                                    className="shrink-0"
                                    priority
                                    loading="eager"
                                />

                        </div>
                        <PermissionGate requiredPermissions={['CREATE_COMMAND_AGENT']}>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 rounded-full text-muted-foreground hover:text-foreground dark:text-white dark:hover:text-white bg-muted hover:bg-primary/60"
                                onClick={() => setShowCreateDialog(true)}
                                title="Create new agent"
                            >
                                <Plus className="w-4 h-4" />
                            </Button>
                        </PermissionGate>
                    </div>
                ) : (
                    <div className="flex justify-center pt-3 pb-1">
                        <Button
                            onClick={toggleSidebar}
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent"
                            title="Expand sidebar"
                        >
                            <PanelLeftOpen className="w-4 h-4" />
                        </Button>
                    </div>
                )}

                {panelExpanded && (
                    <div className="px-[10px] pb-1 space-y-0.5">
                        <PermissionGate requiredPermissions={['CREATE_COMMAND_SESSION']}>
                            <button
                                onClick={handleCreateNewChat}
                                disabled={isCreatingUnfiledSession || !effectiveAgentId}
                                className="w-full flex items-center gap-2.5 px-[10px] py-2 rounded-lg text-sm text-[#1E1E1E] dark:text-white hover:bg-muted/60 transition-colors disabled:opacity-50"
                            >
                                {isCreatingUnfiledSession ? (
                                    <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                                ) : (
                                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F1F1F1]">
                                        <Plus className="w-4 h-4 shrink-0 text-[#1E1E1E]" />
                                    </span>
                                )}
                                <span>New chat</span>
                            </button>
                        </PermissionGate>
                        <div className="relative">
                            <span className="pointer-events-none absolute left-[10px] top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center">
                                <Search className="w-4 h-4 text-[#1E1E1E] dark:text-white" />
                            </span>
                            <input
                                type="text"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-[46px] pr-3 py-2 rounded-lg text-sm bg-transparent text-[#1E1E1E] dark:text-white placeholder:text-[#1E1E1E] dark:placeholder:text-white hover:bg-muted/60 focus:bg-muted/60 focus:outline-none transition-colors"
                            />
                        </div>
                    </div>
                )}

                {!panelExpanded && (
                    <div className="flex flex-col items-center gap-1 px-1 pb-1">
                        <PermissionGate requiredPermissions={['CREATE_COMMAND_SESSION']}>
                            <Button
                                onClick={handleCreateNewChat}
                                variant="ghost"
                                size="icon"
                                disabled={isCreatingUnfiledSession || !effectiveAgentId}
                                className="h-8 w-8"
                                title="New chat"
                            >
                                {isCreatingUnfiledSession ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            </Button>
                        </PermissionGate>
                    </div>
                )}

                {panelExpanded && (
                    <div className="mx-[10px] flex items-center justify-between border-t border-[#E5E5E5] px-0 pb-1 pt-3">
                        <Link
                            href="/workforce/command-team/agents"
                            onClick={closeSidebarIfMobile}
                            className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
                        >
                            My Agents
                        </Link>
                        <Link
                            href="/workforce/command-team/agents"
                            title="Manage agents"
                            aria-label="Manage agents"
                            onClick={closeSidebarIfMobile}
                            className="flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        >
                            <SlidersHorizontal className="size-3.5" />
                        </Link>
                    </div>
                )}
                {panelExpanded && showAgentsEmpty && (
                    <div className="px-[10px] pb-2 pt-1">
                        <div className="flex flex-col items-center gap-2 px-3 py-3 text-center">
                            <div className="flex size-8 items-center justify-center rounded-full bg-muted">
                                <Bot className="size-3.5 text-muted-foreground" />
                            </div>
                            <div className="space-y-0.5">
                                <p className="text-xs font-medium text-foreground">
                                    No agents yet
                                </p>
                                <p className="text-[11px] text-muted-foreground">
                                    Create an agent to start chatting.
                                </p>
                            </div>
                            <PermissionGate requiredPermissions={['CREATE_COMMAND_AGENT']}>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateDialog(true)}
                                    className="mt-0.5 text-[11px] font-medium text-primary underline-offset-2 hover:underline"
                                >
                                    Create agent
                                </button>
                            </PermissionGate>
                        </div>
                    </div>
                )}
                {panelExpanded && !showAgentsEmpty && (
                        <ScrollArea
                            className="min-h-0 min-w-0 w-full"
                            style={
                                agentsScrollHeight > 0
                                    ? { height: `${agentsScrollHeight}px` }
                                    : undefined
                            }
                        >
                            <div ref={agentsListContentRef} className="space-y-0.5 px-[10px] py-1">
                                {showAgentsSkeleton ? (
                                    <div className="space-y-0.5">
                                        {[...Array(3)].map((_, i) => (
                                            <Skeleton key={i} className="h-8 w-full rounded-lg" />
                                        ))}
                                    </div>
                                ) : (
                                    displayedAgents.map((agent: CommandAgent) => {
                                        const isCurrent = agent.id === effectiveAgentId;
                                        const isActivating = activatingAgentId === agent.id;
                                        const isAnotherActivating =
                                            activatingAgentId !== null && !isActivating;
                                        return (
                                            <ContextMenu key={agent.id}>
                                                <ContextMenuTrigger asChild>
                                                    <button
                                                        type="button"
                                                        disabled={isActivating || isAnotherActivating}
                                                        aria-busy={isActivating}
                                                        onClick={() => {
                                                            if (isActivating || isAnotherActivating) return;
                                                            if (!isCurrent) {
                                                                handleActivateAgent(agent);
                                                                return;
                                                            }
                                                            closeSidebarIfMobile();
                                                        }}
                                                        className={cn(
                                                            'flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left transition-colors',
                                                            isCurrent
                                                                ? 'bg-violet-100 font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300'
                                                                : 'text-foreground/80 hover:bg-muted/70',
                                                            isActivating && 'cursor-wait',
                                                            isAnotherActivating && 'opacity-40 cursor-not-allowed',
                                                        )}
                                                    >
                                                        <span
                                                            className={cn(
                                                                'flex size-5 shrink-0 items-center justify-center rounded-md',
                                                                isCurrent
                                                                    ? 'bg-violet-200/70 dark:bg-violet-800/40'
                                                                    : 'bg-muted/60 dark:bg-muted/40',
                                                            )}
                                                        >
                                                            {isActivating ? (
                                                                <Loader
                                                                    size={12}
                                                                    className={isCurrent ? 'text-violet-700 dark:text-violet-300' : 'text-muted-foreground'}
                                                                />
                                                            ) : (
                                                                <Bot
                                                                    className={cn(
                                                                        'size-3',
                                                                        isCurrent
                                                                            ? 'text-violet-700 dark:text-violet-300'
                                                                            : 'text-muted-foreground',
                                                                    )}
                                                                />
                                                            )}
                                                        </span>
                                                        <span className="min-w-0 flex-1 truncate text-[13px]">
                                                            {agent.name}
                                                        </span>
                                                    </button>
                                                </ContextMenuTrigger>
                                                <ContextMenuContent className="w-[160px]">
                                                    <ContextMenuItem onClick={() => handleAgentEdit(agent)} className="cursor-pointer">
                                                        <Settings className="mr-2 h-4 w-4" />Edit Agent
                                                    </ContextMenuItem>
                                                    <ContextMenuSeparator />
                                                    <ContextMenuItem onClick={() => handleAgentDelete(agent)} className="cursor-pointer text-destructive focus:text-destructive">
                                                        <Trash2 className="mr-2 h-4 w-4" />Delete Agent
                                                    </ContextMenuItem>
                                                </ContextMenuContent>
                                            </ContextMenu>
                                        );
                                    })
                                )}
                                {isFetchingNextQuickLaunch && <Skeleton className="h-8 w-full rounded-lg" />}
                                {hasNextQuickLaunch && <div ref={loadMoreQuickLaunchRef} className="h-1" />}
                            </div>
                        </ScrollArea>
                )}

                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    {panelExpanded && !canListProjects && (
                        <div className="mx-[10px] border-t border-[#E5E5E5] px-0 pb-1 pt-2">
                            <p className="text-xs font-medium text-[#717171] dark:text-white">Recent Chats</p>
                        </div>
                    )}
                    {panelExpanded && canListProjects && (
                        <div className="mx-[10px] shrink-0 border-t border-[#E5E5E5]">
                            <CommandProjectsHeader />
                        </div>
                    )}
                    {/* One outer ScrollArea (same as agents) — no nested scrollbar inside projects */}
                    <ScrollArea ref={scrollAreaRef} className="min-h-0 min-w-0 flex-1">
                        <div className="px-[10px]">
                        <AnimatePresence initial={false}>
                        {!canListSessions ? (
                            <div key="sess-denied">
                            {panelExpanded && (
                                <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                                        <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                                    </div>
                                    <p className="text-xs leading-snug text-muted-foreground">No access to sessions</p>
                                </div>
                            )}
                            </div>
                        ) : isLoadingSessions && allSessions.length === 0 && !canListProjects ? (
                            <div key="sess-loading" className="space-y-1 px-1 pt-1">
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className="h-8 w-full rounded-lg" />
                                ))}
                            </div>
                        ) : allSessions.length === 0 && !canListProjects ? (
                            <div key="sess-empty">
                            {panelExpanded && (
                                <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                                        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                                    </div>
                                    <p className="text-xs leading-snug text-muted-foreground">No chats yet. Start a new one!</p>
                                </div>
                            )}
                            </div>
                        ) : canListProjects && panelExpanded ? (
                            <div key="sess-projects" className="min-w-0 pb-2">
                                {canListSessions ? (
                                    <CommandProjectsFolders
                                        activeSessionId={activeSessionId}
                                        onSessionSelect={(sessionId, agentId) => {
                                            onSessionSelect(sessionId, agentId);
                                            closeSidebarIfMobile();
                                        }}
                                        onNewChatInProject={handleCreateNewChatInProject}
                                        onMoveSession={handleMoveSessionToProject}
                                        onDeleteSession={handleDeleteClick}
                                        creatingSessionProjectId={creatingSessionProjectId}
                                        onPinToggle={handlePinToggle}
                                        onStartRename={handleStartEdit}
                                        onExportSession={runExportSession}
                                        editingSessionId={editingSessionId}
                                        editTitle={editTitle}
                                        onEditTitleChange={setEditTitle}
                                        onSaveEdit={handleSaveEdit}
                                        onEditKeyDown={handleKeyDown}
                                        originalTitle={originalTitle}
                                        editInputRef={inputRef}
                                        isUpdatingSession={isUpdatingSession}
                                        isDeletingSession={isDeletingSession}
                                        exportingSessionId={exportingSessionId}
                                        typingSessionId={typingSessionId}
                                        displayedTitle={displayedTitle}
                                    />
                                ) : null}
                                <div className="sticky top-0 z-[1] -mx-[10px] bg-sidebar px-[10px] pb-0.5 backdrop-blur-sm">
                                    <CommandOtherChatsHeader />
                                </div>
                                {isLoadingSessions && allSessions.length === 0 ? (
                                    <div className="space-y-1 px-1 pt-1">
                                        {[...Array(5)].map((_, i) => (
                                            <Skeleton key={i} className="h-8 w-full rounded-lg" />
                                        ))}
                                    </div>
                                ) : (
                                    <CommandOtherChats
                                        showHeader={false}
                                        unfiledSessions={allSessions}
                                        projects={moveProjects}
                                        activeSessionId={activeSessionId}
                                        onSessionSelect={(sessionId, agentId) => {
                                            onSessionSelect(sessionId, agentId);
                                            closeSidebarIfMobile();
                                        }}
                                        onMoveSession={handleMoveSessionToProject}
                                        onDeleteSession={handleDeleteClick}
                                        onPinToggle={handlePinToggle}
                                        onStartRename={handleStartEdit}
                                        onExportSession={runExportSession}
                                        editingSessionId={editingSessionId}
                                        editTitle={editTitle}
                                        onEditTitleChange={setEditTitle}
                                        onSaveEdit={handleSaveEdit}
                                        onEditKeyDown={handleKeyDown}
                                        originalTitle={originalTitle}
                                        editInputRef={inputRef}
                                        isUpdatingSession={isUpdatingSession}
                                        isDeletingSession={isDeletingSession}
                                        exportingSessionId={exportingSessionId}
                                        typingSessionId={typingSessionId}
                                        displayedTitle={displayedTitle}
                                    />
                                )}
                                {isFetchingNextPage && (
                                    <div className="space-y-1 px-1 py-1">
                                        {[...Array(3)].map((_, i) => (
                                            <Skeleton key={i} className="h-8 w-full rounded-lg" />
                                        ))}
                                    </div>
                                )}
                                <div ref={loadMoreRef} className="h-1" />
                            </div>
                        ) : (
                            <motion.div key="sess-list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                            <>
                                {allSessions.map((session, sessionIdx) => {
                                    const isActive = activeSessionId === session.id;
                                    return (
                                    <motion.div
                                        key={session.id}
                                        initial={{ opacity: 0, x: -4 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.15, delay: Math.min(sessionIdx, 10) * 0.025, ease: 'easeOut' }}
                                        onClick={() => {
                                            if (editingSessionId) return;
                                            onSessionSelect(session.id, session.agent_id);
                                            closeSidebarIfMobile();
                                        }}
                                        className={`group relative rounded-lg px-[10px] py-2 mb-0.5 cursor-pointer transition-all duration-150 ${
                                            isActive
                                                ? 'bg-violet-100 text-violet-700 font-medium dark:bg-violet-900/30 dark:text-violet-300'
                                                : 'hover:bg-muted/60 dark:hover:bg-white/5 text-[#1E1E1E] dark:text-white hover:text-[#1E1E1E] dark:hover:text-white'
                                        } ${!panelExpanded ? 'flex items-center justify-center px-0' : ''}`}
                                        title={!panelExpanded ? (session.title ?? undefined) : undefined}
                                    >
                                        {!panelExpanded ? (
                                            <MessageSquare className="w-4 h-4" />
                                        ) : editingSessionId === session.id ? (
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                value={editTitle}
                                                onChange={(e) => setEditTitle(e.target.value)}
                                                onBlur={() => {
                                                    setTimeout(() => {
                                                        if (editingSessionId === session.id && inputRef.current !== document.activeElement) {
                                                            handleSaveEdit(session.id);
                                                        }
                                                    }, 150);
                                                }}
                                                onKeyDown={(e) => handleKeyDown(e, session.id)}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-full text-sm bg-transparent border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                                                placeholder={originalTitle || 'Untitled'}
                                                autoFocus
                                            />
                                        ) : (
                                            <div className="flex items-start justify-between gap-1 min-w-0">
                                                <div className="min-w-0 flex-1 flex flex-col gap-0.5 overflow-hidden">
                                                    {typingSessionId === session.id ? (
                                                        <>
                                                            <p className="truncate text-sm max-w-[160px]">
                                                                {displayedTitle || 'Untitled'}
                                                                <span className="animate-pulse">|</span>
                                                            </p>
                                                            <p
                                                                className="text-[11px] leading-tight text-muted-foreground tabular-nums"
                                                               
                                                            >
                                                                {formatSessionSidebarTime(session.updated_at)}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        (() => {
                                                            const displayTitle = session.title;
                                                            const timeLabel = formatSessionSidebarTime(session.updated_at);
                                                            const titleLine = (
                                                                <p className="truncate text-sm max-w-[160px] leading-snug">
                                                                    {displayTitle || 'Untitled'}
                                                                </p>
                                                            );
                                                            const titleBlock = (
                                                                <div className="flex flex-col gap-0.5 min-w-0">
                                                                    {isMobile ? (
                                                                        titleLine
                                                                    ) : (
                                                                        <Tooltip>
                                                                            <TooltipTrigger asChild>
                                                                                {titleLine}
                                                                            </TooltipTrigger>
                                                                            {isTitleTruncated(displayTitle) && (
                                                                                <TooltipContent side="right" className="max-w-[420px] whitespace-normal break-words">
                                                                                    <p>{displayTitle}</p>
                                                                                </TooltipContent>
                                                                            )}
                                                                        </Tooltip>
                                                                    )}
                                                                    {timeLabel ? (
                                                                        <p
                                                                            className="text-[11px] leading-tight text-muted-foreground tabular-nums"
                                                                            
                                                                        >
                                                                            {timeLabel}
                                                                        </p>
                                                                    ) : null}
                                                                </div>
                                                            );
                                                            return titleBlock;
                                                        })()
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                                                    {session.pinned && (
                                                        <Pin className="w-3 h-3 text-muted-foreground/60 dark:text-white" />
                                                    )}
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <span
                                                                role="button"
                                                                tabIndex={0}
                                                                className="h-5 w-5 shrink-0 rounded-md inline-flex items-center justify-center bg-transparent opacity-100 outline-none ring-0 ring-offset-0 transition-opacity cursor-pointer focus-visible:outline-none focus-visible:ring-0 data-[state=open]:opacity-100 hover:bg-muted-foreground/20 data-[state=open]:bg-muted-foreground/20"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <MoreHorizontal className="h-3.5 w-3.5" />
                                                            </span>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-[160px]">
                                                            <PermissionGate requiredPermissions={['UPDATE_COMMAND_SESSION']}>
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePinToggle(session);
                                                                    }}
                                                                    disabled={isUpdatingSession || editingSessionId === session.id}
                                                                >
                                                                    {session.pinned ? (
                                                                        <><PinOff className="mr-2 h-4 w-4" /> Unpin</>
                                                                    ) : (
                                                                        <><Pin className="mr-2 h-4 w-4" /> Pin</>
                                                                    )}
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleStartEdit(session);
                                                                    }}
                                                                    disabled={editingSessionId === session.id}
                                                                >
                                                                    <Pencil className="mr-2 h-4 w-4" /> Rename
                                                                </DropdownMenuItem>
                                                            </PermissionGate>
                                                            {canListProjects && moveProjects.length > 0 ? (
                                                                <PermissionGate requiredPermissions={['UPDATE_COMMAND_SESSION']}>
                                                                    <DropdownMenuSub>
                                                                        <DropdownMenuSubTrigger
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            disabled={editingSessionId === session.id}
                                                                        >
                                                                            <Folder className="mr-2 h-4 w-4" />
                                                                            Move to project…
                                                                        </DropdownMenuSubTrigger>
                                                                        <DropdownMenuSubContent className="max-h-64 overflow-y-auto">
                                                                            {moveProjects.map((project) => (
                                                                                <DropdownMenuItem
                                                                                    key={project.id}
                                                                                    disabled={session.project_id === project.id}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleMoveSessionToProject(
                                                                                            session.id,
                                                                                            project.id,
                                                                                            session.agent_id
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <Folder className="mr-2 h-3.5 w-3.5" />
                                                                                    {project.name}
                                                                                </DropdownMenuItem>
                                                                            ))}
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem
                                                                                disabled={!session.project_id}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleMoveSessionToProject(
                                                                                        session.id,
                                                                                        null,
                                                                                        session.agent_id
                                                                                    );
                                                                                }}
                                                                            >
                                                                                Other chats
                                                                            </DropdownMenuItem>
                                                                        </DropdownMenuSubContent>
                                                                    </DropdownMenuSub>
                                                                </PermissionGate>
                                                            ) : null}
                                                            <PermissionGate requiredPermissions={['LIST_COMMAND_SESSIONS']}>
                                                                <DropdownMenuSub>
                                                                    <DropdownMenuSubTrigger
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        disabled={
                                                                            exportingSessionId === session.id ||
                                                                            editingSessionId === session.id
                                                                        }
                                                                    >
                                                                        <Download className="mr-2 h-4 w-4" />
                                                                        Export
                                                                    </DropdownMenuSubTrigger>
                                                                    <DropdownMenuSubContent
                                                                        avoidCollisions={false}
                                                                        sideOffset={6}
                                                                        alignOffset={-2}
                                                                        className={cn(
                                                                            'z-[60] min-w-0 overflow-hidden p-0.5 text-xs shadow-lg',
                                                                            'w-[min(10.5rem,calc(100vw-4.5rem))]',
                                                                            'sm:min-w-[168px] sm:w-max sm:p-1 sm:text-sm'
                                                                        )}
                                                                    >
                                                                        <DropdownMenuItem
                                                                            className="gap-1.5 py-1.5 pl-1.5 pr-2 sm:pl-2 [&_svg]:size-3.5 sm:[&_svg]:size-4"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                void runExportSession(session.id, 'MARKDOWN');
                                                                            }}
                                                                            disabled={exportingSessionId === session.id}
                                                                        >
                                                                            <FileText className="mr-0 shrink-0 sm:mr-2" />
                                                                            <span className="sm:hidden">Markdown</span>
                                                                            <span className="hidden sm:inline">
                                                                                Markdown (.md)
                                                                            </span>
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem
                                                                            className="gap-1.5 py-1.5 pl-1.5 pr-2 sm:pl-2 [&_svg]:size-3.5 sm:[&_svg]:size-4"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                void runExportSession(session.id, 'PDF');
                                                                            }}
                                                                            disabled={exportingSessionId === session.id}
                                                                        >
                                                                            <File className="mr-0 shrink-0 sm:mr-2" />
                                                                            PDF
                                                                        </DropdownMenuItem>
                                                                        <DropdownMenuItem
                                                                            className="gap-1.5 py-1.5 pl-1.5 pr-2 sm:pl-2 [&_svg]:size-3.5 sm:[&_svg]:size-4"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                void runExportSession(session.id, 'CLIPBOARD');
                                                                            }}
                                                                            disabled={exportingSessionId === session.id}
                                                                        >
                                                                            <Copy className="mr-0 shrink-0 sm:mr-2" />
                                                                            <span className="sm:hidden">Clipboard</span>
                                                                            <span className="hidden sm:inline">
                                                                                Copy to Clipboard
                                                                            </span>
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuSubContent>
                                                                </DropdownMenuSub>
                                                            </PermissionGate>
                                                            <PermissionGate requiredPermissions={['DELETE_COMMAND_SESSION']}>
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteClick(session.id);
                                                                    }}
                                                                    disabled={isDeletingSession || editingSessionId === session.id}
                                                                    className="text-destructive focus:text-destructive"
                                                                >
                                                                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                                                                </DropdownMenuItem>
                                                            </PermissionGate>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                    </div>
                                                </div>
                                        )}
                                    </motion.div>
                                    );
                                })}
                                {isFetchingNextPage && (
                                    <div className="space-y-1 px-1 py-1">
                                        {[...Array(3)].map((_, i) => (
                                            <Skeleton key={i} className="h-8 w-full rounded-lg" />
                                        ))}
                                    </div>
                                )}
                                <div ref={loadMoreRef} className="h-1" />
                            </>
                            </motion.div>
                        )}
                        </AnimatePresence>
                        </div>
                    </ScrollArea>
                </div>

                <DeleteConfirmationDialog
                    open={deleteDialogOpen}
                    onOpenChange={setDeleteDialogOpen}
                    onConfirm={handleDeleteConfirm}
                    isLoading={isDeletingSession}
                    itemName={sessionToDelete?.title || "session"}
                    itemType="session"
                />

                <DeleteConfirmationDialog
                    open={agentDeleteDialogOpen}
                    onOpenChange={setAgentDeleteDialogOpen}
                    onConfirm={handleAgentDeleteConfirm}
                    isLoading={deleteAgentMutation.isPending}
                    itemName={agentToDelete?.name ?? ''}
                    itemType="command agent"
                />
                <EditCommandAgent
                    agent={editingAgent}
                    open={showEditDialog}
                    onOpenChange={setShowEditDialog}
                />
                <CreateCommandAgent open={showCreateDialog} onOpenChange={setShowCreateDialog} />
        </>
    );

    const hasOpenDialog = showCreateDialog || showEditDialog || deleteDialogOpen || agentDeleteDialogOpen;

    if (isMobile && embeddedMobile) {
        return (
            <TooltipProvider delayDuration={2000}>
                <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-sidebar text-sidebar-foreground">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        {sidebarContent}
                    </div>
                </div>
            </TooltipProvider>
        );
    }

    if (isMobile) {
        return (
            <TooltipProvider delayDuration={2000}>
                <Sheet
                    open={sidebarOpen}
                    onOpenChange={(open) => {
                        if (!open && hasOpenDialog) return;
                        toggleSidebar();
                    }}
                >
                    <SheetContent
                        side="left"
                        className="w-64 p-0 [&>button]:hidden"
                        onPointerDownOutside={(e) => { if (hasOpenDialog) e.preventDefault(); }}
                        onInteractOutside={(e) => { if (hasOpenDialog) e.preventDefault(); }}
                    >
                        <div className="flex h-full flex-col overflow-hidden">
                            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                                {sidebarContent}
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </TooltipProvider>
        );
    }

    return (
        <TooltipProvider delayDuration={2000}>
            <div
                className={`${getWidth()} transition-all duration-300 flex min-h-0 min-w-0 flex-col overflow-hidden`}
            >
                {sidebarContent}
            </div>
        </TooltipProvider>
    );
}

export interface CommandTeamMobileDrawerProps {
    pathname: string;
    teamsHomeHref: string;
    teamsNavLocked: boolean;
    isDarkMode: boolean;
    chatSidebar: React.ReactNode;
}


export function CommandTeamMobileDrawer({
    pathname,
    teamsHomeHref,
    teamsNavLocked,
    isDarkMode,
    chatSidebar,
}: CommandTeamMobileDrawerProps) {
    const { drawerOpen, panel, closeDrawer, expandPlatformNav, backToCommandPanel } = useCommandTeamDrawer();
    const { isBrandingReady, partnerLogoUrl, partnerName, showPoweredBy } = usePartnerBranding();

    const navMain = PlatformData.navMain;

    const railFooterClass =
        '[&_button[data-sidebar=menu-button]]:mx-auto [&_button[data-sidebar=menu-button]]:flex [&_button[data-sidebar=menu-button]]:size-10 [&_button[data-sidebar=menu-button]]:shrink-0 [&_button[data-sidebar=menu-button]]:justify-center [&_button[data-sidebar=menu-button]]:rounded-full [&_button[data-sidebar=menu-button]]:p-0 [&_button[data-sidebar=menu-button]>div.grid]:hidden [&_button[data-sidebar=menu-button]>svg]:hidden [&_.rounded-lg]:!rounded-full';

    return (
        <Sheet open={drawerOpen} onOpenChange={(open) => (!open ? closeDrawer() : undefined)}>
            <SheetContent
                side="left"
                className={cn(
                    'flex h-[var(--app-height,100dvh)] flex-col gap-0 border-0 p-0',
                    '!w-[min(400px,80vw)] !max-w-[min(400px,80vw)] sm:!w-[min(400px,80vw)] sm:!max-w-[min(400px,80vw)]',
                    '[&>button.absolute]:hidden',
                )}
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {panel === 'command' ? (
                    <div className="flex min-h-0 flex-1 flex-row overflow-hidden bg-sidebar text-sidebar-foreground">
                        <aside className="flex w-[52px] shrink-0 flex-col overflow-hidden border-r border-sidebar-border bg-sidebar">
                            <SidebarHeader className="border-b border-sidebar-border/60 p-2">
                                <SidebarMenu>
                                    <SidebarMenuItem>
                                        <SidebarMenuButton
                                            type="button"
                                            size="lg"
                                            className="justify-center px-0 md:h-8 md:p-0"
                                            onClick={expandPlatformNav}
                                            aria-label="Open main AIOS menu"
                                            title="Main menu"
                                        >
                                            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                                                {!isBrandingReady ? (
                                                    <span className="block size-8" />
                                                ) : partnerLogoUrl ? (
                                                    <img
                                                        src={partnerLogoUrl}
                                                        alt={partnerName ?? ''}
                                                        className="size-8 object-contain"
                                                    />
                                                ) : (
                                                    <Image
                                                        src={isDarkMode ? '/Icon_logo_dark.svg' : '/Icon_logo_light.svg'}
                                                        alt=""
                                                        width={200}
                                                        height={200}
                                                    />
                                                )}
                                            </div>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarHeader>
                            <SidebarContent className="min-h-0 flex-1 overflow-y-auto px-0">
                                <SidebarGroup className="p-0">
                                    <SidebarGroupContent className="px-1">
                                        <SidebarMenu>
                                            {navMain.map((item) => {
                                                const isTeamsNavItem =
                                                    item.url === '/workforce';
                                                const locked = teamsNavLocked && isTeamsNavItem;
                                                const isActive =
                                                    item.url === '/workforce/command-team/chat'
                                                        ? (pathname === '/workforce/command-team' ||
                                                              pathname.startsWith('/workforce/command-team/')) &&
                                                          !pathname.startsWith('/workforce/command-team-old')
                                                        : item.url === '/workforce'
                                                          ? pathname.startsWith('/workforce') &&
                                                            !pathname.startsWith('/workforce/command-team-old') &&
                                                            pathname !== '/workforce/command-team' &&
                                                            !pathname.startsWith('/workforce/command-team/')
                                                          : pathname.startsWith(item.url);

                                                if (locked) {
                                                    return (
                                                        <SidebarMenuItem key={item.title}>
                                                            <SidebarMenuButton
                                                                type="button"
                                                                className="cursor-not-allowed justify-center px-0 hover:bg-transparent"
                                                                disabled
                                                                title={item.title}
                                                                aria-disabled
                                                            >
                                                                <item.icon className="text-muted-foreground/55 dark:text-muted-foreground/50" />
                                                            </SidebarMenuButton>
                                                        </SidebarMenuItem>
                                                    );
                                                }

                                                const itemHref = item.url;

                                                return (
                                                    <SidebarMenuItem key={item.title}>
                                                        <Link href={itemHref} title={item.title} onClick={() => closeDrawer()}>
                                                            <SidebarMenuButton
                                                                className={cn(
                                                                    'justify-center px-0 [&>span:last-child]:hidden',
                                                                    isActive
                                                                        ? 'bg-lavender rounded-md font-semibold'
                                                                        : 'hover:bg-muted hover:text-sidebar-accent-foreground rounded-md',
                                                                )}
                                                            >
                                                                <item.icon />
                                                                <span>{item.title}</span>
                                                            </SidebarMenuButton>
                                                        </Link>
                                                    </SidebarMenuItem>
                                                );
                                            })}
                                        </SidebarMenu>
                                    </SidebarGroupContent>
                                </SidebarGroup>
                            </SidebarContent>
                            <SidebarFooter className={cn('mt-auto shrink-0 border-t border-sidebar-border/60 p-2', railFooterClass)}>
                                <NavUser />
                            </SidebarFooter>
                        </aside>

                        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-sidebar">{chatSidebar}</div>
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-sidebar text-sidebar-foreground">
                        <div className="flex shrink-0 items-center gap-2 border-b border-sidebar-border/60 px-3 py-2">
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-9 shrink-0"
                                onClick={backToCommandPanel}
                                aria-label="Back to Command menu"
                            >
                                <ChevronLeft className="size-5" />
                            </Button>
                            <span className="text-sm font-semibold">Menu</span>
                        </div>
                        <SidebarHeader className="border-b border-sidebar-border/60 px-3 pt-4">
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <SidebarMenuButton size="lg" asChild className="md:h-8 md:p-0">
                                        <Link href={teamsHomeHref} onClick={() => closeDrawer()}>
                                            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                                                {!isBrandingReady ? (
                                                    <span className="block size-8" />
                                                ) : partnerLogoUrl ? (
                                                    <img
                                                        src={partnerLogoUrl}
                                                        alt={partnerName ?? ''}
                                                        className="size-8 object-contain"
                                                    />
                                                ) : (
                                                    <Image
                                                        src={isDarkMode ? '/Icon_logo_dark.svg' : '/Icon_logo_light.svg'}
                                                        alt={PlatformData.teams[0].name}
                                                        width={200}
                                                        height={200}
                                                    />
                                                )}
                                            </div>
                                            <div className="grid flex-1 text-left text-lg leading-tight">
                                                <span className="truncate font-semibold">
                                                    {isBrandingReady ? partnerName ?? PlatformData.teams[0].name : ''}
                                                </span>
                                            </div>
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                                {isBrandingReady && showPoweredBy && (
                                    <SidebarMenuItem>
                                        <PoweredByImplementAi isDarkMode={isDarkMode} />
                                    </SidebarMenuItem>
                                )}
                            </SidebarMenu>
                        </SidebarHeader>
                        <SidebarContent className="min-h-0 flex-1 overflow-y-auto">
                            <SidebarGroup>
                                <SidebarGroupContent className="px-1.5 md:px-2">
                                    <SidebarMenu>
                                        {navMain.map((item) => {
                                            const isTeamsNavItem =
                                                item.url === '/workforce';
                                            const locked = teamsNavLocked && isTeamsNavItem;
                                            const isActive =
                                                item.url === '/workforce/command-team/chat'
                                                    ? (pathname === '/workforce/command-team' ||
                                                          pathname.startsWith('/workforce/command-team/')) &&
                                                      !pathname.startsWith('/workforce/command-team-old')
                                                    : item.url === '/workforce'
                                                      ? pathname.startsWith('/workforce') &&
                                                        !pathname.startsWith('/workforce/command-team-old') &&
                                                        pathname !== '/workforce/command-team' &&
                                                        !pathname.startsWith('/workforce/command-team/')
                                                      : pathname.startsWith(item.url);

                                            if (locked) {
                                                return (
                                                    <SidebarMenuItem key={item.title}>
                                                        <SidebarMenuButton
                                                            type="button"
                                                            className="cursor-not-allowed overflow-x-auto px-2.5 hover:bg-transparent md:px-2"
                                                            disabled
                                                            aria-disabled
                                                        >
                                                            <item.icon className="text-muted-foreground/55 dark:text-muted-foreground/50" />
                                                            <div className="flex min-w-0 flex-1 flex-nowrap gap-x-1.5">
                                                                <span className="shrink-0 whitespace-nowrap text-muted-foreground/85">
                                                                    {item.title}
                                                                </span>
                                                                <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-lavender px-1.5 py-1 text-[10px] font-medium text-muted-foreground dark:bg-muted/70">
                                                                    <Lock className="size-2.5" aria-hidden strokeWidth={2} />
                                                                    Requires AIOS Workforce
                                                                </span>
                                                            </div>
                                                        </SidebarMenuButton>
                                                    </SidebarMenuItem>
                                                );
                                            }

                                            const itemHref = item.url;

                                            return (
                                                <SidebarMenuItem key={item.title}>
                                                    <Link href={itemHref} onClick={() => closeDrawer()}>
                                                        <SidebarMenuButton
                                                            className={cn(
                                                                'rounded-md px-2.5 md:px-2',
                                                                isActive
                                                                    ? 'bg-lavender font-semibold'
                                                                    : 'hover:bg-muted hover:text-sidebar-accent-foreground',
                                                            )}
                                                        >
                                                            <item.icon />
                                                            <span>{item.title}</span>
                                                        </SidebarMenuButton>
                                                    </Link>
                                                </SidebarMenuItem>
                                            );
                                        })}
                                    </SidebarMenu>
                                </SidebarGroupContent>
                            </SidebarGroup>
                        </SidebarContent>
                        <SidebarFooter className="mt-auto shrink-0 gap-2 border-t border-sidebar-border/60 p-2">
                            <NavOrg />
                            <NavUser />
                        </SidebarFooter>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
